import { useMemo, useState } from "react";
import { useMastersStore } from "../data/mastersStore";
import type { Notification, NotificationSeverity } from "../data/schemaTypes";
import { Badge, Button, Card, Input, Select } from "../components/ui";

// Alertas page — bandeja de notificaciones. Iconito por severity, filtro
// por leídas/no leídas + severity + tipo. "Marcar leído" individual o en
// bloque. La data viene del mastersStore (persisted localStorage) — cuando
// haya API real, el polling/WS subscription reemplaza el state.
export function AlertsPage() {
  const notifications = useMastersStore((s) => s.notifications);
  const markRead = useMastersStore((s) => s.markNotificationRead);
  const markAllRead = useMastersStore((s) => s.markAllNotificationsRead);

  const [search, setSearch] = useState("");
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [severity, setSeverity] = useState<"" | NotificationSeverity>("");

  const types = useMemo(
    () => Array.from(new Set(notifications.map((n) => n.type))).sort(),
    [notifications],
  );
  const [typeFilter, setTypeFilter] = useState("");

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return notifications
      .filter((n) => {
        if (onlyUnread && n.read_at) return false;
        if (severity && n.severity !== severity) return false;
        if (typeFilter && n.type !== typeFilter) return false;
        if (!q) return true;
        return (
          n.title.toLowerCase().includes(q) ||
          (n.body ?? "").toLowerCase().includes(q) ||
          n.type.toLowerCase().includes(q)
        );
      })
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
  }, [notifications, search, onlyUnread, severity, typeFilter]);

  const unreadCount = notifications.filter((n) => !n.read_at).length;

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-ink">Alertas</h1>
          <p className="text-xs text-sub mt-0.5">
            {rows.length} de {notifications.length} · {unreadCount} sin leer
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          disabled={unreadCount === 0}
          onClick={markAllRead}
        >
          Marcar todas leídas
        </Button>
      </header>

      <Card padding="sm" className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <Input
            label="Buscar"
            placeholder="Título, mensaje o tipo…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-40">
          <Select
            label="Severidad"
            value={severity}
            onChange={(e) =>
              setSeverity(e.target.value as "" | NotificationSeverity)
            }
          >
            <option value="">Todas</option>
            <option value="info">Info</option>
            <option value="warning">Advertencia</option>
            <option value="critical">Crítica</option>
          </Select>
        </div>
        <div className="w-56">
          <Select
            label="Tipo"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">Todos</option>
            {types.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </div>
        <label className="flex items-center gap-2 text-sm text-ink h-8">
          <input
            type="checkbox"
            checked={onlyUnread}
            onChange={(e) => setOnlyUnread(e.target.checked)}
            className="accent-primary"
          />
          <span>Solo no leídas</span>
        </label>
      </Card>

      <Card padding="none">
        <ul className="divide-y divide-border">
          {rows.map((n) => (
            <NotificationRow key={n.id} n={n} onRead={() => markRead(n.id)} />
          ))}
          {rows.length === 0 && (
            <li className="p-8 text-center text-sub text-sm">
              {onlyUnread
                ? "No tienes alertas sin leer 🎉"
                : "No hay alertas que coincidan con los filtros."}
            </li>
          )}
        </ul>
      </Card>
    </div>
  );
}

function NotificationRow({
  n,
  onRead,
}: {
  n: Notification;
  onRead: () => void;
}) {
  const isUnread = !n.read_at;
  return (
    <li
      className={`p-4 flex items-start gap-3 ${isUnread ? "bg-primary/5" : ""}`}
    >
      <SeverityIcon severity={n.severity} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className={`text-sm ${isUnread ? "font-semibold text-ink" : "text-ink"}`}>
            {n.title}
          </h3>
          <Badge variant={severityVariant(n.severity)}>
            {severityLabel(n.severity)}
          </Badge>
          {isUnread && (
            <span className="inline-block w-2 h-2 rounded-full bg-primary" />
          )}
        </div>
        {n.body && (
          <p className="text-sm text-sub mt-1 leading-snug">{n.body}</p>
        )}
        <div className="flex items-center gap-3 mt-2 text-[11px] text-sub">
          <span className="font-mono">{n.type}</span>
          <span>·</span>
          <span>{formatTimeAgo(n.created_at)}</span>
        </div>
      </div>
      {isUnread && (
        <button
          onClick={onRead}
          className="text-xs text-primary hover:underline shrink-0"
        >
          Marcar leída
        </button>
      )}
    </li>
  );
}

function SeverityIcon({ severity }: { severity: NotificationSeverity }) {
  const map: Record<NotificationSeverity, { bg: string; symbol: string }> = {
    info: { bg: "bg-state-in-route", symbol: "i" },
    warning: { bg: "bg-state-delayed", symbol: "!" },
    critical: { bg: "bg-state-failed", symbol: "!" },
  };
  const it = map[severity];
  return (
    <span
      className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full ${it.bg} text-white font-bold text-sm`}
      title={severity}
    >
      {it.symbol}
    </span>
  );
}

function severityVariant(s: NotificationSeverity) {
  return s === "critical" ? "failed" : s === "warning" ? "delayed" : "in-route";
}

function severityLabel(s: NotificationSeverity) {
  return s === "critical" ? "Crítica" : s === "warning" ? "Advertencia" : "Info";
}

function formatTimeAgo(iso: string): string {
  const t = new Date(iso).getTime();
  const diffMin = Math.round((Date.now() - t) / 60000);
  if (diffMin < 1) return "ahora";
  if (diffMin < 60) return `hace ${diffMin} min`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `hace ${diffH} h`;
  const diffD = Math.round(diffH / 24);
  return `hace ${diffD} d`;
}
